import { z } from "zod";

/**
 * Validates data against a Zod schema, returning unvalidated data on failure.
 * @param schema - Zod schema to validate against.
 * @param data - Data to validate.
 * @param context - Label used in error logging.
 * @param preserveExtra - When true, extra properties not in the schema are preserved in the result.
 */
export function safeValidate<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
  context: string,
  preserveExtra = false,
): z.output<T> {
  const result = schema.safeParse(data);

  if (!result.success) {
    console.error(`Zod validation error in ${context}:`, result.error);
    console.warn("Returning unvalidated data due to schema mismatch");
    return data as z.output<T>;
  }

  if (
    preserveExtra &&
    data &&
    typeof data === "object" &&
    !Array.isArray(data)
  ) {
    return { ...data, ...result.data } as z.output<T>;
  }

  return result.data as z.output<T>;
}
